import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  Grid,
  Flex,
  Text,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Center,
  Tooltip,
  Checkbox,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from "@chakra-ui/react";
import AddArrayModal from "./JSONComponent/AddArrayModal";
import ShadowBox from "components/common/ShadowBox/ShadowBox";
import { FaPlus, FaTimes } from "react-icons/fa";
import { CloseIcon } from "@chakra-ui/icons";
import get from "lodash/get";
import CustomInput from "components/common/customInput/CustomInput";

type FieldType = "string" | "number" | "boolean" | "object" | "array";

interface FieldAttributes {
  type: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  format?: string;
  pattern?: string;
  min?: number;
  max?: number;
  enum?: string[];
  items?: FieldAttributes;
  properties?: {
    [key: string]: FieldAttributes;
  };
}

const JSONFormComponent = ({ defaultSchema = {} }: any) => {
  const [fields, setFields] = useState<any>(defaultSchema);
  const [currentField, setCurrentField] = useState<
    { name: string; type: FieldType } & Partial<FieldAttributes>
  >({ name: "", type: "string" });
  const [isAddArray, setIsAddArray] = useState<any>({
    open: false,
    type: "",
    parentKey: null,
    field: false,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCurrentField((prevField) => ({
      ...prevField,
      [name]: value,
    }));
  };

  useEffect(() => {
    console.log("useEffect Fields", fields);
  });

  const handleFieldPropertyChange = (
    name: string,
    value: any,
    path: string
  ) => {
    setFields((prevFields: any) => {
      const updatedFields = { ...prevFields };
      const keys = path.split(".");

      let current = updatedFields;
      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          current[key] = { ...current[key], [name]: value };
        } else {
          current = current[key] = current[key] || {};
        }
      });

      return updatedFields;
    });
  };

  const handleAddField = () => {
    const { name, ...fieldProperties } = currentField;

    if (name) {
      setFields((prevFields: any) => ({
        ...prevFields,
        [name]: fieldProperties,
      }));
      setCurrentField({
        name: "",
        type: "string",
      });
    }
  };

  const handleOpenAddArray = (type: any, parentKey?: any, field?: boolean) => {
    setIsAddArray({
      open: true,
      type: type,
      parentKey: parentKey,
      field: field,
    });
    setIsOpen({ open: false, key: null });
  };

  const handleCloseAddArray = () => {
    setIsAddArray({ open: false, type: "", parentKey: null, field: null });
  };

  const handleExportJson = () => {
    const json = JSON.stringify(fields, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "fields.json";
    link.click();
  };

  const renderFieldProperties = (currentPath: string, currentField: any) => {
    return (
      <>
        <Flex gap={2} alignItems="center">
          <FormLabel fontSize="sm" fontWeight="500">
            Type:
          </FormLabel>
          <Select
            name="type"
            value={currentField.type || ""}
            placeholder="Select Field Type"
            onChange={(e: any) =>
              handleFieldPropertyChange("type", e.target.value, currentPath)
            }
          >
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="boolean">Boolean</option>
            <option value="object">Object</option>
            <option value="array">Array</option>
          </Select>
        </Flex>
        <Grid templateColumns={{ md: "1fr 1fr" }} gap={5} mt={2}>
          {currentField.type === "string" && (
            <>
              <FormControl>
                <CustomInput
                  name="minLength"
                  label="Min Length"
                  type="number"
                  value={currentField.minLength || ""}
                  onChange={(e: any) =>
                    handleFieldPropertyChange(
                      "minLength",
                      e.target.value,
                      currentPath
                    )
                  }
                  floating={true}
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  name="maxLength"
                  label="Max Length"
                  type="number"
                  value={currentField.maxLength || ""}
                  onChange={(e: any) =>
                    handleFieldPropertyChange(
                      "maxLength",
                      e.target.value,
                      currentPath
                    )
                  }
                  floating={true}
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  name="format"
                  label="Format"
                  value={currentField.format || ""}
                  onChange={(e: any) =>
                    handleFieldPropertyChange(
                      "format",
                      e.target.value,
                      currentPath
                    )
                  }
                  floating
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  floating
                  name="pattern"
                  label="Pattern"
                  value={currentField.pattern || ""}
                  onChange={(e: any) =>
                    handleFieldPropertyChange(
                      "pattern",
                      e.target.value,
                      currentPath
                    )
                  }
                />
              </FormControl>

              <Checkbox
                name="required"
                isChecked={currentField.required ?? false}
                onChange={(e: any) =>
                  handleFieldPropertyChange(
                    "required",
                    e.target.checked,
                    currentPath
                  )
                }
              >
                Required{" "}
                <Text as="span" color="red.500" ml={1}>
                  *
                </Text>
              </Checkbox>
            </>
          )}

          {currentField.type === "number" && (
            <>
              <FormControl>
                <CustomInput
                  floating={true}
                  name="min"
                  label="Min Value"
                  value={currentField.min || ""}
                  onChange={(e: any) =>
                    handleFieldPropertyChange(
                      "min",
                      e.target.value,
                      currentPath
                    )
                  }
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  floating={true}
                  name="max"
                  label="Max Value"
                  value={currentField.max || ""}
                  onChange={(e: any) =>
                    handleFieldPropertyChange(
                      "max",
                      e.target.value,
                      currentPath
                    )
                  }
                />
              </FormControl>
            </>
          )}

          {currentField.type === "boolean" && (
            <Checkbox
              name="required"
              isChecked={currentField.required ?? false}
              onChange={(e: any) =>
                handleFieldPropertyChange(
                  "required",
                  e.target.checked,
                  currentPath
                )
              }
            >
              Required{" "}
              <Text as="span" color="red.500" ml={1}>
                *
              </Text>
            </Checkbox>
          )}

          {currentField.type === "object" && (
            <FormControl>
              {/* <FormLabel>Properties</FormLabel>
              <Input
                name="properties"
                value={JSON.stringify(currentField.properties ?? {}, null, 2)}
                onChange={(e: any) =>
                  handleFieldPropertyChange(
                    "properties",
                    e.target.value,
                    currentPath
                  )
                }
                placeholder="Enter nested properties as JSON"
              /> */}
            </FormControl>
          )}

          {currentField.type === "array" && (
            <FormControl>
              <FormLabel>Item Type</FormLabel>
              <Select
                name="items"
                value={currentField.items?.type || ""}
                onChange={(e: any) =>
                  handleFieldPropertyChange(
                    "items",
                    e.target.value,
                    currentPath
                  )
                }
              >
                <option value="string">String</option>
                <option value="number">Number</option>
                <option value="boolean">Boolean</option>
                <option value="object">Object</option>
              </Select>
            </FormControl>
          )}
        </Grid>
      </>
    );
  };

  const updateFields = (path: string, value: any) => {
    const keys = path.split(".");
    const lastKey = keys.pop()!;
    const parent = keys.reduce((acc, key) => acc[key], fields);
    parent[lastKey] = value;
    setFields({ ...fields });
  };

  const addArray = (parentKey: any, arrayName: string) => {
    setFields((prev: any) => ({
      ...prev,
      [arrayName]: [],
    }));
  };

  const addObjectToArray = (arrayName: string, index?: number) => {
    const newObject = {
      name: "",
      type: "",
    };

    setFields((prev: any) => {
      const updatedArray = [...(prev[arrayName] || [])];

      if (index !== undefined && index >= 0 && index <= updatedArray.length) {
        updatedArray.splice(index, 0, newObject);
      } else {
        updatedArray.push(newObject);
      }

      return {
        ...prev,
        [arrayName]: updatedArray,
      };
    });
  };

  const addObjectToObjectArray = (path: string) => {
    setFields((prevFields: any) => {
      const updatedFields = { ...prevFields };
      const pathSegments = path.split(".");
      let current = updatedFields;

      pathSegments.forEach((segment, index) => {
        if (!current[segment]) {
          current[segment] = index === pathSegments.length - 1 ? [] : {};
        }
        current = current[segment];
      });

      current.push({
        name: "",
        type: "string",
      });

      return updatedFields;
    });
  };

  const deleteObjectFromObjectArray = (fieldPath: string, index: number) => {
    const pathArray = fieldPath.split(".");

    let parentArray = fields;

    for (let i = 0; i < pathArray.length - 1; i++) {
      parentArray = parentArray[pathArray[i]];
    }

    parentArray.splice(index, 1);

    setFields({ ...fields });
  };

  const renderField = (
    field: any,
    index: number,
    arrayName: string,
    parentKey: string,
    key: string,
    nested?: boolean
  ) => {
    const fieldPath = nested ? arrayName : `${arrayName}.${index}`;

    return (
      <ShadowBox mt={3} w="100%">
        <Flex justifyContent="end">
          <Tooltip label="Delete" placement="left" hasArrow>
            <Box
              mt="-20px"
              mr="-30px"
              color={"red.400"}
              _hover={{
                borderColor: "red.500",
                color: "red.600",
              }}
              onClick={() => deleteObjectFromObjectArray(`${fieldPath}`, index)}
              rounded={"full"}
              w="1.75rem"
            >
              <CloseIcon fontSize="xs" />
            </Box>
          </Tooltip>
        </Flex>

        <Grid
          templateColumns={{ md: "1fr 1fr" }}
          key={fieldPath}
          gap={5}
          mt={2}
        >
          <CustomInput
            name="FieldName"
            placeholder="Field Name"
            value={field.name}
            onChange={(e) => updateFields(`${fieldPath}.name`, e.target.value)}
          />
          <Select
            mt={2}
            placeholder="Field Type"
            value={field.type}
            onChange={(e) => updateFields(`${fieldPath}.type`, e.target.value)}
          >
            <option value="string">String</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
            <option value="email">Email</option>
            <option value="boolean">Boolean</option>
            <option value="object">Object</option>
          </Select>

          {field.type === "string" && (
            <>
              <FormControl>
                <CustomInput
                  floating={true}
                  name="minLength"
                  type="number"
                  label="Min Length"
                  value={field.minLength || ""}
                  onChange={(e) =>
                    updateFields(`${fieldPath}.minLength`, e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  floating={true}
                  name="maxLength"
                  label="Max Length"
                  type="number"
                  value={field.maxLength || ""}
                  onChange={(e) =>
                    updateFields(`${fieldPath}.maxLength`, e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  floating={true}
                  label="Format"
                  name="format"
                  value={field.format || ""}
                  onChange={(e) =>
                    updateFields(`${fieldPath}.format`, e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  label="Pattern"
                  floating
                  name="pattern"
                  value={field.pattern || ""}
                  onChange={(e) =>
                    updateFields(`${fieldPath}.pattern`, e.target.value)
                  }
                />
              </FormControl>
              <Checkbox
                name="required"
                isChecked={field.required || false}
                onChange={(e) =>
                  updateFields(`${fieldPath}.required`, e.target.checked)
                }
              >
                Required{" "}
                <Text as="span" color="red.500" ml={1}>
                  *
                </Text>
              </Checkbox>
            </>
          )}
          {field.type === "number" && (
            <>
              <FormControl>
                <CustomInput
                  floating={true}
                  name="min"
                  label="Min Value"
                  value={field.min || ""}
                  onChange={(e: any) =>
                    updateFields(`${fieldPath}.min`, e.target.value)
                  }
                />
              </FormControl>
              <FormControl>
                <CustomInput
                  floating={true}
                  name="max"
                  label="Max Value"
                  value={field.max || ""}
                  onChange={(e: any) =>
                    updateFields(`${fieldPath}.max`, e.target.value)
                  }
                />
              </FormControl>
            </>
          )}
          {field.type === "boolean" && (
            <Checkbox
              isChecked={field.required ?? false}
              onChange={(e: any) =>
                updateFields(`${fieldPath}.required`, e.target.checked)
              }
            >
              Required{" "}
              <Text as="span" color="red.500" ml={1}>
                *
              </Text>{" "}
              <Text as="span" color="red.500" ml={1}>
                *
              </Text>
            </Checkbox>
          )}
          {(field.type === "email" || field.type === "date") && (
            <FormControl>
              <CustomInput
                floating={true}
                name="format"
                label="Format
                "
                value={field.format || ""}
                onChange={(e: any) =>
                  updateFields(`${fieldPath}.format`, e.target.value)
                }
              />
            </FormControl>
          )}
        </Grid>
        {field.type === "object" && (
          <Box>
            {field.fields?.map((subField: any, subIndex: number) => (
              <>
                {renderField(
                  subField,
                  subIndex,
                  `${fieldPath}.fields`,
                  parentKey,
                  key
                )}
              </>
            ))}
            <Flex alignItems="center" justifyContent="end" mt={3} gap={3}>
              <Button
                colorScheme="green"
                onClick={() => addObjectToObjectArray(`${fieldPath}.fields`)}
              >
                <FaPlus />
                &nbsp; Field
              </Button>
            </Flex>
          </Box>
        )}

        {/* {field.type === "array" && (
          <>
            {field.items?.map((item: any, itemIndex: number) =>
              renderField(item, itemIndex, `${fieldPath}.items`)
            )}
            <Button onClick={() => addObjectToArray(`${fieldPath}.items`)}>
              Add Object to Array
            </Button>
          </>
        )} */}
        <Flex justifyContent="end">
          <Popover placement="left">
            <PopoverTrigger>
              <Button
                ml={5}
                h="38px"
                mt="8px"
                color="white"
                _hover={{
                  bg: "teal.500",
                }}
                bg="teal.400"
              >
                <FaPlus />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <PopoverArrow />
              <PopoverCloseButton />
              <PopoverHeader>Select Position </PopoverHeader>
              <PopoverBody>
                <Flex alignItems="center" justifyContent="center" gap={2}>
                  <Button
                    onClick={() =>
                      nested
                        ? addObjectToArrayInObject(parentKey, key, index)
                        : addObjectToArray(arrayName, index)
                    }
                    color="white"
                    _hover={{
                      bg: "teal.500",
                    }}
                    bg="teal.400"
                  >
                    Add Above
                  </Button>

                  <Button
                    onClick={() =>
                      nested
                        ? addObjectToArrayInObject(parentKey, key, index + 1)
                        : addObjectToArray(arrayName, index + 1)
                    }
                    color="white"
                    _hover={{
                      bg: "teal.500",
                    }}
                    bg="teal.400"
                  >
                    Add Below
                  </Button>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </Flex>
      </ShadowBox>
    );
  };

  // action button
  const [isOpen, setIsOpen] = useState<any>({ open: false, key: null });

  const handleToggle = (key: any) => {
    setIsOpen({ open: !isOpen.open, key: key });
  };

  // nested obj

  const updateObjectFields = (currentFields: any, key: string, value: any) => {
    currentFields[key] = value;
    setFields({ ...fields });
  };

  const updateArrObjectFields = (
    currentFields: any,
    fieldPath: string,
    value: any
  ) => {
    const keys = fieldPath.split(".");
    let obj = currentFields;

    for (let i = 0; i < keys.length - 1; i++) {
      obj = obj[keys[i]];
    }

    obj[keys[keys.length - 1]] = value;
    setFields({ ...currentFields });
  };

  const addNestedObject = (parentKey: any, objectName: string) => {
    setFields((prev: any) => ({
      ...prev,
      [objectName]: {},
    }));
  };

  const addArrayToObject = (parentKey: string, arrayName: any) => {
    console.log("fields in func", fields);

    setFields((prev: any) => {
      const updatedFields = { ...prev };

      const getNestedObject: any = (keys: string[], obj: any) => {
        if (keys.length === 0) return obj;
        const key = keys.shift();
        if (!obj[key!]) {
          obj[key!] = {};
        }
        return getNestedObject(keys, obj[key!]);
      };

      const keys = parentKey.split(".");
      const targetObject = getNestedObject(keys, updatedFields);

      if (!targetObject[arrayName]) {
        targetObject[arrayName] = [];
      }

      return updatedFields;
    });
  };

  const addObjectToObject = (
    parentKey: string,
    nestedObjectName: any,
    field?: boolean
  ) => {
    console.log("fields in func", fields);
    setFields((prev: any) => {
      const updatedFields = { ...prev };

      const getNestedObject: any = (keys: string[], obj: any) => {
        if (keys.length === 0) return obj;
        const key = keys.shift();
        if (!obj[key!]) {
          obj[key!] = {};
        }
        return getNestedObject(keys, obj[key!]);
      };

      const keys = parentKey.split(".");
      const targetObject = getNestedObject(keys, updatedFields);

      if (!targetObject[nestedObjectName]) {
        targetObject[nestedObjectName] = field ? { type: "string" } : {};
      }

      return updatedFields;
    });
  };

  const deleteObjectFromObject = (fieldPath: string) => {
    console.log("path", fieldPath);
    const pathArray = fieldPath.split(".");
    const keyToDelete = pathArray.pop();
    const parentPath = pathArray.join(".");

    if (!keyToDelete) return;
    if (parentPath) {
      // For nested paths
      const parentObject = get(fields, parentPath);

      if (parentObject && typeof parentObject === "object") {
        delete parentObject[keyToDelete];

        setFields({
          ...fields,
          [parentPath]: { ...parentObject },
        });
      }
    } else {
      const updatedFields = { ...fields };
      delete updatedFields[keyToDelete];

      setFields(updatedFields);
    }
  };

  const addObjectToArrayInObject = (
    parentKey: string,
    arrayName: string,
    index?: number
  ) => {
    console.log("addObj", parentKey, arrayName, index);
    const newObject = {
      name: "",
      type: "",
    };

    setFields((prev: any) => {
      const updatedFields = { ...prev };

      const keys = parentKey.split(".");
      let current = updatedFields;

      keys.forEach((key) => {
        if (!current[key]) {
          current[key] = {};
        }
        current = current[key];
      });

      if (!Array.isArray(current[arrayName])) {
        current[arrayName] = [];
      }

      if (
        index !== undefined &&
        index >= 0 &&
        index <= current[arrayName].length
      ) {
        current[arrayName].splice(index, 0, newObject);
      } else {
        current[arrayName].push(newObject);
      }

      return updatedFields;
    });
  };

  const renderObjectField = (
    field: any,
    key: string,
    parentKey?: string,
    nested?: boolean
  ) => {
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (Array.isArray(field)) {
      return (
        <Accordion allowMultiple>
          <AccordionItem>
            <h2>
              <AccordionButton>
                <Box as="span" flex="1" textAlign="left" fontWeight="700">
                  {key}
                </Box>
                <AccordionIcon />
              </AccordionButton>
            </h2>
            <AccordionPanel p={2}>
              <ShadowBox w="100%">
                <Flex justifyContent="end">
                  <Tooltip label="Delete" placement="left" hasArrow>
                    <Box
                      mt="-20px"
                      mr="-30px"
                      color={"red.400"}
                      _hover={{
                        borderColor: "red.500",
                        color: "red.600",
                      }}
                      onClick={() => deleteObjectFromObject(fullKey)}
                      rounded={"full"}
                      w="1.75rem"
                    >
                      <CloseIcon fontSize="xs" />
                    </Box>
                  </Tooltip>
                </Flex>
                <Grid key={fullKey} gap={3}>
                  {field.map((item: any, index: number) => (
                    <VStack
                      key={`${fullKey}.${index}`}
                      spacing={1}
                      align="start"
                    >
                      <FormLabel>{item.name}</FormLabel>
                      {renderField(
                        item,
                        index,
                        `${fullKey}.${index}`,
                        parentKey || key,
                        key,
                        true
                      )}
                    </VStack>
                  ))}
                </Grid>
                <Flex justifyContent="end" alignItems="center" mt={3}>
                  <Button
                    bg="#fade57"
                    color="white"
                    _hover={{ bg: "#f3d129" }}
                    size={{ base: "sm", md: "md" }}
                    onClick={() =>
                      addObjectToArrayInObject(parentKey || key, key)
                    }
                  >
                    <FaPlus />
                    &nbsp; Object to {key}
                  </Button>
                </Flex>
              </ShadowBox>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      );
    } else if (typeof field === "object" && field !== null) {
      if (nested) {
        return (
          <>
            {/* <FormLabel>{key}</FormLabel> */}
            <ShadowBox w="100%">
              <Flex justifyContent="end">
                <Tooltip label="Delete" placement="left" hasArrow>
                  <Box
                    mt="-20px"
                    mr="-30px"
                    color={"red.400"}
                    _hover={{
                      borderColor: "red.500",
                      color: "red.600",
                    }}
                    onClick={() => deleteObjectFromObject(fullKey)}
                    rounded={"full"}
                    w="1.75rem"
                  >
                    <CloseIcon fontSize="xs" />
                  </Box>
                </Tooltip>
              </Flex>
              <Grid
                key={fullKey}
                gap={2}
                p={{ base: 1, md: 2 }}
                w="100%"
                overflowX="auto"
                className="customScrollBar"
              >
                {renderFieldProperties(fullKey, field)}

                {Object.keys(field).map((subKey) =>
                  renderObjectField(field[subKey], subKey, fullKey)
                )}

                <Box
                  display={{ base: "grid", md: "flex" }}
                  gap={3}
                  justifyContent="end"
                  alignItems="center"
                >
                  <Button
                    bg="#fd7474"
                    color="white"
                    size={{ base: "sm", md: "md" }}
                    _hover={{ bg: "#fd5858" }}
                    onClick={() => handleOpenAddArray("arrayObject", fullKey)}
                  >
                    <FaPlus />
                    &nbsp; Array to {key}
                  </Button>
                  <Button
                    bg="#79dd79"
                    color="white"
                    size={{ base: "sm", md: "md" }}
                    _hover={{ bg: "#4ac34a" }}
                    onClick={() => handleOpenAddArray("objectObject", fullKey)}
                  >
                    <FaPlus />
                    &nbsp; Object to {key}
                  </Button>
                  <Button
                    bg="#7a9cff"
                    color="white"
                    size={{ base: "sm", md: "md" }}
                    _hover={{ bg: "#5c83f7" }}
                    onClick={() =>
                      handleOpenAddArray("objectObject", fullKey, true)
                    }
                  >
                    <FaPlus />
                    &nbsp; Field to {key}
                  </Button>
                </Box>
              </Grid>
            </ShadowBox>
          </>
        );
      } else {
        return (
          <>
            <Accordion allowMultiple>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left" fontWeight="700">
                      {key}
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel px={2} py={4}>
                  <ShadowBox w="100%">
                    <Flex justifyContent="end">
                      <Tooltip label="Delete" placement="left" hasArrow>
                        <Box
                          mt="-20px"
                          mr="-30px"
                          color={"red.400"}
                          _hover={{
                            borderColor: "red.500",
                            color: "red.600",
                          }}
                          onClick={() => deleteObjectFromObject(fullKey)}
                          rounded={"full"}
                          w="1.75rem"
                        >
                          <CloseIcon fontSize="xs" />
                        </Box>
                      </Tooltip>
                    </Flex>
                    <Grid
                      key={fullKey}
                      gap={2}
                      // p={2}
                      w="100%"
                      overflowX="auto"
                      className="customScrollBar"
                      overflow="visible"
                    >
                      {renderFieldProperties(fullKey, field)}

                      {Object.keys(field).map((subKey) =>
                        renderObjectField(field[subKey], subKey, fullKey)
                      )}
                      <Box position="relative" overflow="visible">
                        <Flex
                          flexDirection="column"
                          alignItems="flex-end"
                          position="relative"
                          zIndex={100}
                          overflow="visible"
                        >
                          {isOpen.open && isOpen.key === fullKey && (
                            <Box
                              position="absolute"
                              top="-100px"
                              // right="-28px"
                              // zIndex={10000}
                              display="flex"
                              flexDirection="column"
                              alignItems="flex-end"
                              gap={1}
                            >
                              <Flex
                                gap={3}
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text
                                  whiteSpace="nowrap"
                                  bg="gray.200"
                                  p={1}
                                  fontSize="sm"
                                  fontWeight="500"
                                >
                                  Array to {key}
                                </Text>
                                <Button
                                  bg="#fd7474"
                                  color="white"
                                  h="1.75rem"
                                  size="xs"
                                  rounded={"full"}
                                  _hover={{ bg: "#fd5858" }}
                                  onClick={() =>
                                    handleOpenAddArray("arrayObject", fullKey)
                                  }
                                  // p={0}
                                  pointerEvents="auto"
                                >
                                  <FaPlus />
                                </Button>
                              </Flex>

                              <Flex
                                gap={3}
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text
                                  whiteSpace="nowrap"
                                  bg="gray.200"
                                  p={1}
                                  fontSize="sm"
                                  fontWeight="500"
                                >
                                  Object to {key}
                                </Text>
                                <Button
                                  bg="#79dd79"
                                  color="white"
                                  _hover={{ bg: "#4ac34a" }}
                                  h="1.75rem"
                                  size="xs"
                                  rounded={"full"}
                                  onClick={() =>
                                    handleOpenAddArray("objectObject", fullKey)
                                  }
                                  borderRadius="full"
                                  pointerEvents="auto"
                                >
                                  <FaPlus />
                                </Button>
                              </Flex>

                              <Flex
                                gap={3}
                                alignItems="center"
                                justifyContent="center"
                              >
                                <Text
                                  whiteSpace="nowrap"
                                  bg="gray.200"
                                  p={1}
                                  fontSize="sm"
                                  fontWeight="500"
                                >
                                  Field to {key}
                                </Text>
                                <Button
                                  bg="#7a9cff"
                                  color="white"
                                  _hover={{ bg: "#5c83f7" }}
                                  h="1.75rem"
                                  size="xs"
                                  rounded={"full"}
                                  onClick={() => {
                                    handleOpenAddArray(
                                      "objectObject",
                                      fullKey,
                                      true
                                    );
                                  }}
                                  borderRadius="full"
                                  pointerEvents="auto"
                                >
                                  <FaPlus />
                                </Button>
                              </Flex>
                            </Box>
                          )}
                          <Button
                            bg={
                              isOpen.open && isOpen.key === fullKey
                                ? "#fd3d33"
                                : "#3b5998"
                            }
                            color="white"
                            borderRadius="full"
                            h="1.75rem"
                            size="xs"
                            rounded={"full"}
                            _hover={{
                              bg:
                                isOpen.open && isOpen.key === fullKey
                                  ? "#d5332a"
                                  : "#3b3d8a",
                            }}
                            // top="23px"
                            // left="28px"
                            pointerEvents="auto"
                            onClick={() => handleToggle(fullKey)}
                            zIndex={100}
                            position="relative"
                          >
                            {isOpen.open && isOpen.key === fullKey ? (
                              <FaTimes />
                            ) : (
                              <FaPlus />
                            )}
                          </Button>
                        </Flex>
                      </Box>
                    </Grid>
                  </ShadowBox>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </>
        );
      }
    } else {
      return <></>;
    }
  };

  return (
    <Box position="relative">
      {isOpen.open && (
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          background="rgba(255, 255, 255, 0.2)"
          backdropFilter="blur(2px)"
          zIndex={9}
        />
      )}
      <Box
        // position="relative"
        zIndex={1}
        pointerEvents={isOpen.open ? "none" : "auto"}
      >
        <Flex justifyContent="end" alignItems="center">
          <Button colorScheme="blue" onClick={handleExportJson}>
            Export JSON
          </Button>
        </Flex>
        <Grid templateColumns={{ md: "1.5fr 1fr" }} gap={5} mt={2}>
          <ShadowBox w="100%">
            <Box h="75vH" overflowX="auto" w="100%" className="customScrollBar">
              <Grid gap={3}>
                {/* <VStack spacing={4} w="100%"> */}
                {Object.keys(fields).map((arrayName) => (
                  <Accordion allowMultiple>
                    <AccordionItem>
                      <h2>
                        <AccordionButton
                          bg="teal.50"
                          _hover={{ bg: "teal.100" }}
                        >
                          <Box
                            as="span"
                            flex="1"
                            textAlign="left"
                            fontWeight="700"
                          >
                            {arrayName}
                          </Box>
                          <AccordionIcon />
                        </AccordionButton>
                      </h2>
                      <AccordionPanel p={0}>
                        <VStack key={arrayName} align="start" p="10px">
                          {Array.isArray(fields[arrayName]) ? (
                            <ShadowBox w="100%">
                              <Flex justifyContent="end">
                                <Tooltip
                                  label="Delete"
                                  placement="left"
                                  hasArrow
                                >
                                  <Box
                                    mt="-20px"
                                    mr="-30px"
                                    color={"red.400"}
                                    _hover={{
                                      borderColor: "red.500",
                                      color: "red.600",
                                    }}
                                    onClick={() =>
                                      deleteObjectFromObject(arrayName)
                                    }
                                    rounded={"full"}
                                    w="1.75rem"
                                  >
                                    <CloseIcon fontSize="xs" />
                                  </Box>
                                </Tooltip>
                              </Flex>
                              <Grid gap={3}>
                                {fields[arrayName].map(
                                  (field: any, index: number) =>
                                    renderField(
                                      field,
                                      index,
                                      arrayName,
                                      arrayName,
                                      field
                                    )
                                )}
                              </Grid>
                              <Flex justifyContent="end" alignItems="center">
                                <Button
                                  bg="#fade57"
                                  color="white"
                                  _hover={{ bg: "#f3d129" }}
                                  size={{ base: "sm", md: "md" }}
                                  onClick={() => addObjectToArray(arrayName)}
                                  mt={3}
                                >
                                  <FaPlus />
                                  &nbsp; Object to {arrayName}
                                </Button>
                              </Flex>
                            </ShadowBox>
                          ) : (
                            renderObjectField(
                              fields[arrayName],
                              arrayName,
                              "",
                              true
                            )
                          )}
                        </VStack>
                      </AccordionPanel>
                    </AccordionItem>
                  </Accordion>
                ))}
                <Center gap={3} mb={6}>
                  <Button
                    color="white"
                    _hover={{
                      bg: "teal.400",
                    }}
                    bg="teal.300"
                    onClick={() => handleOpenAddArray("object")}
                  >
                    Add Object
                  </Button>
                  <Button
                    color="white"
                    _hover={{
                      bg: "teal.400",
                    }}
                    bg="teal.300"
                    onClick={() => handleOpenAddArray("array")}
                  >
                    Add Array
                  </Button>
                </Center>

                <AddArrayModal
                  isOpen={isAddArray.open}
                  onClose={handleCloseAddArray}
                  onSubmit={
                    isAddArray.type === "array"
                      ? addArray
                      : isAddArray.type === "object"
                      ? addNestedObject
                      : isAddArray.type === "objectObject"
                      ? addObjectToObject
                      : addArrayToObject
                  }
                  fieldType={
                    isAddArray.type === "array" ||
                    isAddArray.type === "arrayObject"
                      ? "Array"
                      : "Object"
                  }
                  parentKey={isAddArray.parentKey}
                  isField={isAddArray.field}
                />
                {/* </VStack> */}
              </Grid>
            </Box>
          </ShadowBox>
          <ShadowBox>
            <Box
              as="pre"
              overflowX="auto"
              maxH="75vH"
              className="customScrollBar"
            >
              {JSON.stringify(fields, null, 2)}
            </Box>
          </ShadowBox>
        </Grid>
      </Box>
    </Box>
  );
};

export default JSONFormComponent;
